import { useNavigate } from 'react-router-dom';
import { Avatar, Bar, Icon, Ring, SectionLabel, MobileHeader, type IconName } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useCertificates, useCourses, useEnrollments } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';

type TodoItem = { n: number; l: string; th: string; ic: IconName; t: 'sage' | 'spark' };

function formatDateBadge(value?: string) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const d = new Date(year, month - 1, day);
  return {
    month: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(d).toUpperCase(),
    day: new Intl.DateTimeFormat('en-US', { day: '2-digit' }).format(d),
  };
}

export function MobileHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { L } = useLocalized();
  const { data: courses = [] } = useCourses();
  const { data: enrollments = [] } = useEnrollments();
  const { data: certs = [] } = useCertificates();
  const enrolledCourseIds = new Set(enrollments.map((e) => e.courseId));

  const inProgress = courses.filter((c) => enrollments.some((e) => e.courseId === c.id && e.status !== 'completed'));
  const next = inProgress[0];
  const nextEnr = next && enrollments.find((e) => e.courseId === next.id);
  const featured = !next ? (courses.find((c) => c.status === 'active' && !enrolledCourseIds.has(c.id)) ?? null) : null;
  const allTodoItems: TodoItem[] = next && nextEnr ? [
    { n: next.lessons.filter((l) => !l.done).length, l: 'Lessons left', th: 'บทเรียน', ic: 'video', t: 'sage' },
    { n: next.requirements.includes('posttest') && nextEnr.posttest == null ? 1 : 0, l: 'Post-test due', th: 'แบบทดสอบ', ic: 'doc', t: 'spark' },
    { n: next.requirements.includes('eval') && !nextEnr.eval ? 1 : 0, l: 'Evaluation', th: 'แบบประเมิน', ic: 'star', t: 'sage' },
    { n: next.requirements.includes('attend') && !nextEnr.attended ? 1 : 0, l: 'Check-in', th: 'เช็คอิน', ic: 'qr', t: 'spark' },
  ] : [];
  const todoItems = allTodoItems.filter((x) => x.n > 0);
  const upcoming = courses.find((c) => (c.type === 'onsite' || c.type === 'hybrid') && !!c.seminar && enrolledCourseIds.has(c.id));
  const upcomingDate = formatDateBadge(upcoming?.seminarDate);
  const validCerts = certs.filter((c) => c.status === 'valid').length;
  const expiring = certs.filter((c) => c.status === 'expiring').length;
  const certPct = certs.length ? Math.round((validCerts / certs.length) * 100) : 0;

  // Staff with console access get clear switches to their console(s) — not a
  // role switch, they're a learner too. Admin/superadmin oversee both the admin
  // and the instructor console; instructors get the instructor console.
  const consoles: { label: string; to: string }[] =
    user?.role === 'superadmin'
      ? [{ label: 'Admin console', to: '/admin' }, { label: 'Instructor console', to: '/teach' }]
      : user?.role === 'admin'
        ? [{ label: 'Generate invites', to: '/admin/invites' }]
        : user?.role === 'instructor'
          ? [{ label: 'Instructor console', to: '/teach' }]
          : [];

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader
        title={`สวัสดี, ${user?.lineName ?? user?.firstEn}`}
        th={`${user?.role} · ${user?.unitId}`}
        right={
          <Avatar initials={user?.initials ?? 'SC'} size={38} tone={user?.tone} photoUrl={user?.photoUrl} />
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '20px 20px 28px' }}>
        {consoles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {consoles.map((c) => (
              <button key={c.to} className="btn btn-soft btn-block" onClick={() => navigate(c.to)}>
                <Icon name="shield" size={16} /> {c.label} <Icon name="arrowR" size={16} />
              </button>
            ))}
          </div>
        )}
        {/* Certificate hero */}
        <div className="card" style={{ padding: 20, background: 'linear-gradient(135deg, var(--sage-tint), var(--card) 60%, var(--spark-tint))' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <span className="eyebrow">Certificate status · สถานะใบรับรอง</span>
            <Icon name="shield" size={18} style={{ color: 'var(--sage)' }} />
          </div>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <Ring value={certPct} size={64} stroke={6} label={`${validCerts}/${certs.length}`} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.35 }}>{validCerts} valid certificates</div>
              <div style={{ fontSize: 13, color: 'var(--spark-deep)', marginTop: 3, fontWeight: 500, display: 'flex', gap: 6, alignItems: 'center' }}>
                <Icon name="clock" size={14} /> {expiring} expiring within 60 days
              </div>
            </div>
          </div>
          <button className="btn btn-soft btn-block" style={{ marginTop: 16 }} onClick={() => navigate('/learn/wallet')}>
            Open certificate wallet <Icon name="arrowR" size={16} />
          </button>
        </div>

        {/* Start here — new learner with nothing in progress */}
        {featured && (
          <div>
            <SectionLabel>Start here · เริ่มที่นี่</SectionLabel>
            <button onClick={() => navigate(`/learn/catalog/${featured.id}`)} style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}>
              <div className="card" style={{ overflow: 'hidden' }}>
                <div style={{ aspectRatio: '3 / 1', background: featured.coverUrl ? `${featured.cover} center/cover no-repeat url("${featured.coverUrl}")` : featured.cover }} />
                <div style={{ padding: 16 }}>
                  <div className="th" style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.3 }}>{L(featured.title)}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>{featured.title.en}</div>
                  <p style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-2)' }}>{featured.summary}</p>
                  <div style={{ marginTop: 12, display: 'flex', gap: 6, alignItems: 'center', color: 'var(--sage-deep)', fontSize: 13.5, fontWeight: 600 }}>
                    View course <Icon name="arrowR" size={15} />
                  </div>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Continue learning */}
        {next && nextEnr && (
          <div>
            <SectionLabel action={<button onClick={() => navigate('/learn/learning')} style={{ fontSize: 13, color: 'var(--sage-deep)', border: 'none', background: 'transparent', cursor: 'pointer' }}>All</button>}>
              Continue learning
            </SectionLabel>
            <button onClick={() => navigate(`/learn/catalog/${next.id}`)} style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}>
              <div className="card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', gap: 14 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 12, background: next.logoUrl ? `var(--spark-soft) center/contain no-repeat url("${next.logoUrl}")` : 'var(--spark-soft)', flex: 'none', display: 'grid', placeItems: 'center', border: next.logoUrl ? '1px solid var(--line)' : 'none' }}>
                    {!next.logoUrl && <Icon name="play" size={18} fill style={{ color: 'var(--spark-deep)' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="th" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{L(next.title)}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>Next: {next.lessons.find((l) => !l.done)?.title.en}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 14 }}>
                  <Bar value={nextEnr.progress} style={{ flex: 1 }} />
                  <span className="u-mono" style={{ fontSize: 12, color: 'var(--sage-deep)', fontWeight: 500 }}>{nextEnr.progress}%</span>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Upcoming onsite */}
        {upcoming && (
          <div>
            <SectionLabel>Upcoming onsite · ภาคปฏิบัติ</SectionLabel>
            <div className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <div style={{ width: 54, flex: 'none', textAlign: 'center', background: 'var(--spark-soft)', borderRadius: 11, padding: '8px 0' }}>
                  {upcomingDate ? (
                    <>
                      <div className="u-mono" style={{ fontSize: 11, color: 'var(--spark-deep)', fontWeight: 600 }}>{upcomingDate.month}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, letterSpacing: '-.02em', color: 'var(--spark-deep)' }}>{upcomingDate.day}</div>
                    </>
                  ) : (
                    <Icon name="cal" size={24} style={{ margin: '7px auto', color: 'var(--spark-deep)' }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="th" style={{ fontSize: 15, fontWeight: 600 }}>{L(upcoming.title)}</div>
                  <div style={{ display: 'flex', gap: 10, fontSize: 12.5, color: 'var(--ink-3)', marginTop: 5 }}>
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}><Icon name="pin" size={13} /> {upcoming.venue}</span>
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}><Icon name="clock" size={13} /> {upcoming.seminar}</span>
                  </div>
                </div>
                <button className="btn btn-sm btn-spark" onClick={() => navigate(`/learn/course/${upcoming.id}/attendance`)}>
                  <Icon name="qr" size={15} /> QR
                </button>
              </div>
            </div>
          </div>
        )}

        {/* To-do tiles */}
        {todoItems.length > 0 && (
        <div>
          <SectionLabel>To complete · สิ่งที่ต้องทำ</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {todoItems.map((x) => (
              <button key={x.l} onClick={() => navigate('/learn/learning')} className="card" style={{ width: '100%', textAlign: 'left', cursor: 'pointer', padding: 15, font: 'inherit', color: 'inherit' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                  <span style={{ width: 34, height: 34, borderRadius: 9, background: `var(--${x.t}-soft)`, color: `var(--${x.t}-deep)`, display: 'grid', placeItems: 'center' }}>
                    <Icon name={x.ic} size={18} />
                  </span>
                  <span style={{ fontSize: 24, fontWeight: 600, lineHeight: 1 }}>{x.n}</span>
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{x.l}</div>
                <div className="th" style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{x.th}</div>
              </button>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
